/**
 * Inventory Service
 *
 * Data access layer for inventory_items table.
 * Handles all inventory-related database operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';
import { mergeInventoryItemTranslation } from '../lib/translations';
import { fetchAllPages, fetchAllPagesByChunks, dedupeById } from '../lib/fetchAllPages';
import type { Database } from '../lib/database.types';

type InventoryItem = Database['public']['Tables']['inventory_items']['Row'];
type InventoryItemInsert = Database['public']['Tables']['inventory_items']['Insert'];
type InventoryItemUpdate = Database['public']['Tables']['inventory_items']['Update'];

/**
 * Extended inventory item with product info
 */
export interface InventoryItemWithProduct extends InventoryItem {
  product_name?: string;
  product_type?: string;
  location_id?: string;
  location_name?: string;
  is_currently_rented?: boolean;
  is_currently_blocked?: boolean;
}

/**
 * Fetch all inventory items with optional filters
 *
 * @param accessControl - User's access control info for merchant/location filtering
 * @param filters - Optional filters for inventory items
 * @param language - Language code for translations (default: 'en')
 * @param defaultLanguage - Merchant's default language. When equal to
 *   `language` or unset, translation merge is skipped.
 */
export async function getInventoryItems(
  accessControl: AccessControl | null,
  filters?: {
    productTypeId?: string;
    locationId?: string;
    active?: boolean;
    searchQuery?: string;
    /** Also match the barcode column (only when the merchant has scanning on). */
    searchBarcode?: boolean;
  },
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    // Paged: a merchant's fleet can exceed the PostgREST row cap, and a single
    // unbounded request would drop the tail of the list with no error.
    // `allocation_order` is not unique, so `id` is added as the tie-breaker
    // that gives the pages a deterministic boundary.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('inventory_items')
        .select(`
        *,
        product_types!inventory_items_product_type_id_fkey!inner(product_name, product_type, location_id, locations!inner(location_name, merchant_id))
      `)
        .order('allocation_order', { ascending: true })
        .order('id', { ascending: true });

      // Apply access control filtering on nested product_types.locations
      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'product_types.locations.merchant_id',
        locationIdColumn: 'product_types.location_id'
      });

      // Apply user filters
      if (filters?.locationId) {
        query = query.eq('product_types.location_id', filters.locationId);
      }

      if (filters?.productTypeId) {
        query = query.eq('product_type_id', filters.productTypeId);
      }

      if (filters?.active !== undefined) {
        query = query.eq('active', filters.active);
      }

      if (filters?.searchQuery) {
        query = query.or(
          `item_number.ilike.%${filters.searchQuery}%,combination_code.ilike.%${filters.searchQuery}%` +
          (filters.searchBarcode ? `,barcode.ilike.%${filters.searchQuery}%` : '')
        );
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // The three lookups below are resolved in one batched query each, chunked
    // so the `.in()` list stays inside the URL limit and paged so no chunk is
    // truncated. They used to run per item inside Promise.all — tolerable while
    // the list was capped at 1000, but lifting the cap turns that into one
    // request per item (two once translations are on), which would make the
    // page slower than the truncation it fixes.
    const nowIso = new Date().toISOString();
    const itemIds: string[] = (data || []).map((item: any) => item.id);

    // Items blocked right now (maintenance / blocking period spanning "now").
    const blockingTimes = await fetchAllPagesByChunks<any, string>(
      itemIds,
      (ids, from, to) =>
        supabase
          .from('inventory_blocking_times')
          .select('id, inventory_item_id')
          .in('inventory_item_id', ids)
          .lte('blocked_from', nowIso)
          .gt('blocked_until', nowIso)
          .order('id', { ascending: true })
          .range(from, to)
    );
    const blockedItemIds = new Set<string>(
      blockingTimes.map((bt: any) => bt.inventory_item_id)
    );

    // Items out on an active or upcoming-but-started rental.
    const rentedRows = await fetchAllPagesByChunks<any, string>(
      itemIds,
      (ids, from, to) =>
        supabase
          .from('booking_items')
          .select('id, inventory_item_id, bookings!inner(booking_state, end_date)')
          .in('inventory_item_id', ids)
          .in('bookings.booking_state', ['confirmed', 'active'])
          .gte('bookings.end_date', nowIso)
          .order('id', { ascending: true })
          .range(from, to)
    );
    const rentedItemIds = new Set<string>(
      rentedRows.map((r: any) => r.inventory_item_id)
    );

    // Skip translation merge for the default language — default language
    // values are stored in the main table.
    const translationByItem = new Map<string, any>();
    if (defaultLanguage && language !== defaultLanguage) {
      const translations = await fetchAllPagesByChunks<any, string>(
        itemIds,
        (ids, from, to) =>
          supabase
            .from('inventory_item_translations')
            .select('*')
            .in('inventory_item_id', ids)
            .eq('language_code', language)
            .order('inventory_item_id', { ascending: true })
            .range(from, to)
      );
      for (const tr of translations) {
        translationByItem.set(tr.inventory_item_id, tr);
      }
    }

    const itemsWithStatus = (data || []).map((item: any) => {
      const itemWithTranslation =
        defaultLanguage && language !== defaultLanguage
          ? mergeInventoryItemTranslation(item, translationByItem.get(item.id) ?? null)
          : item;

      return {
        ...itemWithTranslation,
        product_name: (item.product_types as any)?.product_name,
        product_type: (item.product_types as any)?.product_type,
        location_id: (item.product_types as any)?.location_id,
        location_name: (item.product_types as any)?.locations?.location_name,
        is_currently_rented: rentedItemIds.has(item.id),
        is_currently_blocked: blockedItemIds.has(item.id),
      };
    });

    return { data: itemsWithStatus, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single inventory item by ID
 *
 * @param id - Inventory item ID
 * @param accessControl - User's access control info for merchant/location filtering
 * @param language - Language code for translations (default: 'en')
 * @param defaultLanguage - Merchant's default language. When equal to
 *   `language` or unset, translation merge is skipped.
 */
export async function getInventoryItemById(
  id: string,
  accessControl: AccessControl | null,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('inventory_items')
      .select(`
        *,
        product_types!inventory_items_product_type_id_fkey!inner(product_name, product_type, inventory_type, description, picture_url, location_id, locations!inner(location_name, pickup_address, merchant_id))
      `)
      .eq('id', id);

    // Apply access control filtering
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'product_types.locations.merchant_id',
      locationIdColumn: 'product_types.location_id'
    });

    const { data, error } = await query.single();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    // Skip translation merge for the default language —
    // default language values are stored in the main table.
    let itemWithTranslation: any = data;
    if (defaultLanguage && language !== defaultLanguage) {
      const { data: translation } = await supabase
        .from('inventory_item_translations')
        .select('*')
        .eq('inventory_item_id', data.id)
        .eq('language_code', language)
        .maybeSingle();

      itemWithTranslation = mergeInventoryItemTranslation(data, translation);
    }

    return {
      data: itemWithTranslation,
      error: null,
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new inventory item
 */
export async function createInventoryItem(item: InventoryItemInsert) {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .insert(item)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Item tags and `nr` values already used under a product type. Both are unique
 * per product type, so the duplicate and multi-copy flows read this before
 * choosing identifiers for new items.
 */
export async function getInventoryItemIdentifiers(productTypeId: string) {
  try {
    const rows = await fetchAllPages<{ id: string; item_number: string; nr: number }>((from, to) =>
      supabase
        .from('inventory_items')
        .select('id, item_number, nr')
        .eq('product_type_id', productTypeId)
        .order('id', { ascending: true })
        .range(from, to)
    );
    const unique = dedupeById(rows);
    return {
      data: {
        itemNumbers: unique.map((r) => r.item_number).filter((n): n is string => typeof n === 'string'),
        nrs: unique.map((r) => r.nr).filter((n): n is number => typeof n === 'number'),
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create several inventory items in one request. PostgREST runs the insert as
 * a single statement, so either every row is created or none is — a tag or
 * barcode clash in row 7 does not leave rows 1–6 behind.
 */
export async function createInventoryItems(items: InventoryItemInsert[]) {
  try {
    if (items.length === 0) return { data: [] as InventoryItem[], error: null };
    const { data, error } = await supabase
      .from('inventory_items')
      .insert(items)
      .select();

    if (error) throw error;

    return { data: (data || []) as InventoryItem[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an existing inventory item
 */
export async function updateInventoryItem(id: string, updates: InventoryItemUpdate) {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete an inventory item (soft delete by setting active to false)
 */
export async function deleteInventoryItem(id: string) {
  try {
    // First check if this inventory item has any bookings
    const { data: bookingItems, error: bookingCheckError } = await supabase
      .from('booking_items')
      .select('id')
      .eq('inventory_item_id', id)
      .limit(1);

    if (bookingCheckError) throw bookingCheckError;

    // If there are bookings, prevent deletion
    if (bookingItems && bookingItems.length > 0) {
      return {
        data: null,
        error: 'Cannot delete this inventory item because it has associated bookings. Please deactivate it instead.'
      };
    }

    // No bookings found, safe to delete
    const { error } = await supabase
      .from('inventory_items')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { data: null, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get available inventory items for a product within a date range
 */
export async function getAvailableInventory(
  productTypeId: string,
  startDate: string,
  endDate: string
) {
  try {
    // Both queries are paged: a truncated item list would hide stock, and a
    // truncated booking list would offer items that are already out.
    const allItems = dedupeById(
      await fetchAllPages<any>((from, to) =>
        supabase
          .from('inventory_items')
          .select('*')
          .eq('product_type_id', productTypeId)
          .eq('active', true)
          .order('id', { ascending: true })
          .range(from, to)
      )
    );

    // Get all bookings that overlap with the requested date range
    const overlappingBookings = await fetchAllPages<any>((from, to) =>
      supabase
        .from('booking_items')
        .select(`
        id,
        inventory_item_id,
        bookings!inner(booking_state, start_date, end_date)
      `)
        .eq('product_type_id', productTypeId)
        .in('bookings.booking_state', ['confirmed', 'active'])
        .lte('bookings.start_date', endDate)
        .gte('bookings.end_date', startDate)
        .order('id', { ascending: true })
        .range(from, to)
    );

    // Filter out items that are booked
    const bookedItemIds = new Set(
      overlappingBookings.map((b: any) => b.inventory_item_id)
    );
    const availableItems = allItems.filter(
      (item: any) => !bookedItemIds.has(item.id)
    );

    return { data: availableItems, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get inventory statistics
 *
 * @param accessControl - User's access control info for merchant/location filtering
 */
export async function getInventoryStats(accessControl: AccessControl | null) {
  try {
    // Paged: these are whole-fleet counters, so a truncated fetch would report
    // a smaller fleet than the merchant owns on the dashboard.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('inventory_items')
        .select(`
        id,
        active,
        product_types!inventory_items_product_type_id_fkey!inner(locations!inner(merchant_id))
      `)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'product_types.locations.merchant_id',
      });

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Items currently blocked (maintenance / blocking period overlapping "now")
    // must not be counted as available. Resolved in one chunked, paged query —
    // the id list is now as long as the whole fleet.
    const activeItems = (data || []).filter((item: any) => item.active === true);
    const nowIso = new Date().toISOString();
    const blockingTimes = await fetchAllPagesByChunks<any, string>(
      activeItems.map((item: any) => item.id),
      (ids, from, to) =>
        supabase
          .from('inventory_blocking_times')
          .select('id, inventory_item_id')
          .in('inventory_item_id', ids)
          .lte('blocked_from', nowIso)
          .gt('blocked_until', nowIso)
          .order('id', { ascending: true })
          .range(from, to)
    );
    const blockedItemIds = new Set<string>(
      blockingTimes.map((bt: any) => bt.inventory_item_id)
    );

    const stats = {
      total: data?.length || 0,
      active: activeItems.length,
      inactive: data?.filter((item: any) => item.active === false).length || 0,
      blocked: blockedItemIds.size,
      available: activeItems.filter((item: any) => !blockedItemIds.has(item.id)).length,
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Upsert inventory item translation
 * Creates or updates a translation for a specific language
 */
export async function upsertInventoryItemTranslation(
  inventoryItemId: string,
  languageCode: string,
  translation: {
    inventory_specific_info?: string | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('inventory_item_translations')
      .upsert(
        {
          inventory_item_id: inventoryItemId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'inventory_item_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for an inventory item
 */
export async function getInventoryItemTranslations(inventoryItemId: string) {
  try {
    const { data, error } = await supabase
      .from('inventory_item_translations')
      .select('*')
      .eq('inventory_item_id', inventoryItemId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete an inventory item translation row for a given language.
 * Used when a translation is cleared in the editor so the empty value is
 * actually removed instead of leaving the previous row in place.
 */
export async function deleteInventoryItemTranslation(
  inventoryItemId: string,
  languageCode: string
) {
  try {
    const { error } = await supabase
      .from('inventory_item_translations')
      .delete()
      .eq('inventory_item_id', inventoryItemId)
      .eq('language_code', languageCode);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
